import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import "../dash.css";
import debounce from "lodash.debounce";
import { useLoading } from "../LoadingContext";
import AdminNavbar from "./AdminNavbar";
import Pagination from "../../Pagination";

function AdminDashboard() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setData] = useState([]);
  const [buttonData, setButtonData] = useState([]);
  const [showButtonData, setShowButtonData] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const { loading, setLoading } = useLoading(true); // Set to true initially
  const [sortField, setSortField] = useState(""); // Sorting field
  const [sortOrder, setSortOrder] = useState("asc"); // Sorting order: "asc" or "desc"

  const debouncedSearch = debounce((searchQuery) => {
    setSearchQuery(searchQuery);
  }, 500);

  const handleSearch = (e) => {
    debouncedSearch(e.target.value);
    setSearch(e.target.value);
  };

  const handleAdd = (event) => {
    event.preventDefault();
    navigate("/add_client");
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/client-api/clients/`
        );

        if (Array.isArray(response.data.data)) {
          // Filter the data where `is_active` is true
          const activeData = response.data.data.filter(
            (item) => item.is_active
          );

          setData(activeData); // Set only the active data
        } else {
          console.error("Data is not an array:", response.data);
        }
      } catch (error) {
        console.log(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchButtonData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/client-api/clients/deactivated/`
        );
        setButtonData(response.data.data);
      } catch (error) {
        console.log(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchButtonData();
  }, []);

  const handleDelete = async (id) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to deactivate this Client?"
    );
    if (isConfirmed) {
      try {
        await axios.patch(
          `${process.env.REACT_APP_API_BASE_URL}/client-api/clients/${id}/update/`,
          {
            is_active: false,
          }
        );
        setData(data.filter((item) => item.client_id !== id));
        window.location.reload();
      } catch (error) {
        console.log(error.message);
      }
    }
  };

  if (loading) {
    return (
      <div
        className="container d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div className="text-center">
          <div
            className="spinner-border text-dark"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <h3 className="text- text-dark">&nbsp;Data is Fetching.... </h3>
      </div>
    );
  }

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
  };

  const getSortArrow = (field) => {
    if (sortField !== field) return "▲▼";
    return sortOrder === "asc" ? "▲" : "▼";
  };

  const sortedItems = [...data].sort((a, b) => {
    if (sortField) {
      if (sortOrder === "asc") {
        return a[sortField] > b[sortField] ? 1 : -1;
      } else {
        return a[sortField] < b[sortField] ? 1 : -1;
      }
    }
    return 0;
  });

  const filteredItems = showButtonData
    ? buttonData.filter(
        (item) =>
          (item.id?.toString() || "").includes(searchQuery) ||
          (item.client_id?.toString() || "").includes(searchQuery) ||
          (item.client_name?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.client_country?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.client_city?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.client_contact_number?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.client_mail_id?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.client_contact_person?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.client_contact_person_mail_id?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (
            item.client_contact_person_phone_number?.toLowerCase() || ""
          ).includes(searchQuery.toLowerCase()) ||
          (item.engagement_type?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          )
      )
    : sortedItems.filter(
        (item) =>
          (item.id?.toString() || "").includes(searchQuery) ||
          (item.client_id?.toString() || "").includes(searchQuery) ||
          (item.client_name?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.client_country?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.client_city?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.client_contact_number?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.client_mail_id?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.client_contact_person?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (item.client_contact_person_mail_id?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          ) ||
          (
            item.client_contact_person_phone_number?.toLowerCase() || ""
          ).includes(searchQuery.toLowerCase()) ||
          (item.engagement_type?.toLowerCase() || "").includes(
            searchQuery.toLowerCase()
          )
      );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const handleDownload = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/client-api/clients/download-csv/`,
        {
          method: "GET",
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Screenify_Client_Data.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        console.error("Download failed");
      }
    } catch (error) {
      console.error("An error occurred during download", error);
    }
  };

  return (
    <>
      <AdminNavbar />
      <div className="container py-3 main-content">
        <div className="p-2 mb-1 rounded">
          <h3 className="text-center font_header">Admin Dashboard</h3>

          <div className="row align-items-center mt-1 mb-4">
            <div className="col-12 col-lg-4 mb-2 mb-lg-0">
              <div className="search">
                <form className="form-inline text-dark fw-bold">
                  <div className="col-lg-7 col-md-5">
                    <label htmlFor="search">Search:</label>
                    <input
                      className="form-control mt-1 border-dark text-dark"
                      type="search"
                      title="Search"
                      aria-label="Search"
                      onChange={handleSearch}
                      value={search}
                    />
                  </div>
                </form>
              </div>
            </div>
            <div className="col-12 col-lg-8 text-lg-end mt-3">
              <button
                className="btn btn-sm btn-outline-dark mb-2 mx-2"
                title="Add Client"
                onClick={handleAdd}
              >
                Add Client
              </button>
              <button
                className="btn btn-sm btn-outline-dark mb-2"
                onClick={() => setShowButtonData(!showButtonData)}
              >
                {showButtonData
                  ? "Show Active Client"
                  : "Show Deactivated Client"}
              </button>
              <button
                className="btn btn-sm btn-outline-dark mb-2 mx-2"
                title="Download Client Data"
                onClick={handleDownload}
              >
                Download
              </button>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="table-responsive table-wrapper text-center">
            <table
              className="table table-hover table-bordered border-dark"
              style={{
                tableLayout: "fixed",
                width: "100%",
                minWidth: "800px",
              }}
            >
              <thead className="border-dark tb_header">
                <tr>
                  <th
                    className={sortField === "id" ? "sorted" : ""}
                    onClick={() => handleSort("id")}
                    style={{
                      width: "100px",
                      cursor: "pointer",
                      backgroundColor: "#ced4da",
                      color: "#000",
                      backgroundImage: "none",
                      border: "black",
                    }}
                  >
                    ID
                    {getSortArrow("id")}
                  </th>
                  <th
                    className={sortField === "id" ? "sorted" : ""}
                    onClick={() => handleSort("client_id")}
                    style={{
                      width: "150px",
                      cursor: "pointer",
                      backgroundColor: "#ced4da",
                      color: "#000",
                      backgroundImage: "none",
                    }}
                  >
                    Client&nbsp;ID
                    {getSortArrow("client_id")}
                  </th>
                  <th
                    onClick={() => handleSort("client_name")}
                    style={{
                      width: "200px",
                      cursor: "pointer",
                      backgroundColor: "#ced4da",
                      color: "#000",
                      backgroundImage: "none",
                    }}
                  >
                    Name
                    <span className="sort-arrow">
                      {getSortArrow("client_name")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("client_country")}
                    style={{
                      width: "150px",
                      cursor: "pointer",
                      backgroundColor: "#ced4da",
                      color: "#000",
                      backgroundImage: "none",
                    }}
                  >
                    Country
                    {getSortArrow("client_country")}
                  </th>
                  <th
                    onClick={() => handleSort("client_city")}
                    style={{
                      width: "150px",
                      cursor: "pointer",
                      backgroundColor: "#ced4da",
                      color: "#000",
                      backgroundImage: "none",
                    }}
                  >
                    City
                    {getSortArrow("client_city")}
                  </th>
                  <th
                    onClick={() => handleSort("client_contact_number")}
                    style={{
                      width: "200px",
                      cursor: "pointer",
                      backgroundColor: "#ced4da",
                      color: "#000",
                      backgroundImage: "none",
                    }}
                  >
                    Contact&nbsp;Number
                    {getSortArrow("phone_number")}
                  </th>
                  <th
                    onClick={() => handleSort("client_mail_id")}
                    style={{
                      width: "300px",
                      cursor: "pointer",
                      backgroundColor: "#ced4da",
                      color: "#000",
                      backgroundImage: "none",
                    }}
                  >
                    Mail&nbsp;ID
                    {getSortArrow("client_mail_id")}
                  </th>
                  <th
                    onClick={() => handleSort("client_contact_person")}
                    style={{
                      width: "200px",
                      cursor: "pointer",
                      backgroundColor: "#ced4da",
                      color: "#000",
                      backgroundImage: "none",
                    }}
                  >
                    Contact&nbsp;Person
                    {getSortArrow("client_contact_person")}
                  </th>
                  <th
                    onClick={() => handleSort("client_contact_person_mail_id")}
                    style={{
                      width: "300px",
                      cursor: "pointer",
                      backgroundColor: "#ced4da",
                      color: "#000",
                      backgroundImage: "none",
                    }}
                  >
                    Contact&nbsp;Person&nbsp;Mail&nbsp;Id
                    {getSortArrow("client_contact_person_mail_id")}
                  </th>
                  <th
                    onClick={() =>
                      handleSort("client_contact_person_phone_number")
                    }
                    style={{
                      width: "300px",
                      cursor: "pointer",
                      backgroundColor: "#ced4da",
                      color: "#000",
                      backgroundImage: "none",
                    }}
                  >
                    Contact&nbsp;Person&nbsp;Phone&nbsp;Number
                    {getSortArrow("client_contact_person_phone_number")}
                  </th>
                  <th
                    onClick={() => handleSort("engagement_type")}
                    style={{
                      width: "200px",
                      cursor: "pointer",
                      backgroundColor: "#ced4da",
                      color: "#000",
                      backgroundImage: "none",
                    }}
                  >
                    Engagement&nbsp;Type
                    {getSortArrow("engagement_type")}
                  </th>
                  {!showButtonData && (
                    <th
                      style={{
                        width: "100px",
                        backgroundColor: "#ced4da",
                        color: "#000",
                        backgroundImage: "none",
                      }}
                    >
                      Actions
                    </th>
                  )}
                </tr>
              </thead>

              <tbody className="text-center table-white border-dark">
                {currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr key={item.client_id}>
                      <td>{item.id}</td>
                      <td>{item.client_id}</td>
                      <td>{item.client_name}</td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.client_country}
                      </td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.client_city}
                      </td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.client_contact_number}
                      </td>
                      <td>{item.client_mail_id}</td>
                      <td>{item.client_contact_person}</td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.client_contact_person_mail_id}
                      </td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.client_contact_person_phone_number}
                      </td>
                      <td
                        style={{ whiteSpace: "normal", wordWrap: "break-word" }}
                      >
                        {item.engagement_type}
                      </td>
                      {!showButtonData && (
                        <td>
                          <Link
                            to={`/add_client_copy/${item.id}`}
                            className="copy"
                            title="Copy"
                            data-toggle="tooltip"
                          >
                            <i className="fa fa-copy"></i>
                          </Link>
                          <Link
                            to={`/edit_client/${item.id}`}
                            className="edit"
                            title="Edit"
                            data-toggle="tooltip"
                          >
                            <i className="fa fa-edit ms-1 me-1"></i>
                          </Link>
                          <Link
                            to="#"
                            className="delete"
                            title="Deactivate Client Data"
                            data-toggle="tooltip"
                            onClick={() => handleDelete(item.id)}
                          >
                            <i className="fa fa-trash text-danger"></i>
                          </Link>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="15" className="text-start">
                      <h4>No data available</h4>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination Code */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            paginate={(page) => setCurrentPage(page)}
          />
        </div>
      </div>
    </>
  );
}

export default AdminDashboard;
